import { useCallback, useEffect, useRef, useState } from 'react'
import posthog, { FeatureFlagOverrides } from 'posthog-js'
import { IS_PROD } from 'constants/envs'
import { FEATURE_FLAGS, FEATURE_FLAG_VARIANTS } from 'constants/tracking'
import type { FeatureFlagDetail } from 'posthog-js'
import styles from './FeatureFlagOverlay.module.scss'
import { FLAG_LABEL } from './constants'
import { flagValueToString, parseInputValue } from './utils'

const FeatureFlagOverlay = () => {
  // Local state
  const [isOpen, setIsOpen] = useState(false)
  const [flagDetails, setFlagDetails] = useState<Record<string, FeatureFlagDetail>>({})
  const [overrides, setOverrides] = useState<FeatureFlagOverrides>({})
  const [inputValues, setInputValues] = useState<Record<string, string>>({})

  // Refs
  const panelRef = useRef<HTMLDivElement>(null)

  /**
   * Refreshes the feature flag details from PostHog and updates the state accordingly.
   * It also ensures that input values are seeded for any flags that don't have an input value yet.
   */
  const refreshFlags = useCallback(() => {
    const details = posthog.featureFlags.getFlagsWithDetails()
    setFlagDetails(details)
    setInputValues((prev) => {
      const next = { ...prev }
      // Seed inputs for any flag we don't have an input for yet
      const allKeys = new Set([...Object.values(FEATURE_FLAGS), ...Object.keys(details)])
      allKeys.forEach((key) => {
        if (!(key in next)) {
          next[key] = flagValueToString(details[key])
        }
      })
      return next
    })
  }, [])

  useEffect(() => {
    refreshFlags()
    const unsubscribe = posthog.onFeatureFlags(refreshFlags)
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe()
    }
  }, [refreshFlags])

  // Close panel when clicking outside
  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen])

  if (IS_PROD) return null

  // Constants
  const allFlagKeys = [...new Set([...Object.values(FEATURE_FLAGS), ...Object.keys(flagDetails)])]
  const isLoaded = Object.keys(flagDetails).length > 0

  /**
   * Applies an override for a specific feature flag, updating the overrides state and notifying PostHog of the change.
   * @param {string} flagKey
   * @param {string} rawValue
   */
  const applyOverride = (flagKey: string, rawValue?: string) => {
    const value = rawValue ?? inputValues[flagKey] ?? ''
    const newOverrides: FeatureFlagOverrides = { ...overrides, [flagKey]: parseInputValue(value) }
    setOverrides(newOverrides)
    posthog.featureFlags.overrideFeatureFlags({ flags: newOverrides })
  }

  /**
   * Handles the change event for a select input, updating the input value and applying or resetting the override based on the selected value.
   * @param {string} flagKey
   * @param {string} value
   */
  const handleSelectChange = (flagKey: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [flagKey]: value }))
    if (value === '') {
      resetOverride(flagKey)
    } else {
      applyOverride(flagKey, value)
    }
  }

  /**
   * Resets the override for a specific feature flag, removing it from the overrides and resetting the input value to the server value.
   * @param {string} flagKey
   */
  const resetOverride = (flagKey: string) => {
    const { [flagKey]: _, ...rest } = overrides
    setOverrides(rest)
    posthog.featureFlags.overrideFeatureFlags(Object.keys(rest).length ? { flags: rest } : false)
    // Reset input to server value
    setInputValues((prev) => ({ ...prev, [flagKey]: flagValueToString(flagDetails[flagKey]) }))
  }

  /**
   * Reset all overrides and input values to their server values.
   */
  const resetAll = () => {
    setOverrides({})
    posthog.featureFlags.overrideFeatureFlags(false)
    setInputValues((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((key) => {
        next[key] = flagValueToString(flagDetails[key])
      })
      return next
    })
  }

  return (
    <div ref={panelRef} className={styles.container}>
      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.header}>
            <span className={styles.title}>Feature Flags</span>
            {!isLoaded && <span className={styles.badge}>not loaded</span>}
            {Object.keys(overrides).length > 0 && (
              <button className={styles['reset-all']} onClick={resetAll}>
                Reset all
              </button>
            )}
          </div>

          <ul className={styles.list}>
            {allFlagKeys.map((flagKey) => {
              const detail = flagDetails[flagKey]
              const isOverridden = flagKey in overrides
              const serverValue = flagValueToString(detail)

              return (
                <li key={flagKey} className={`${styles.row} ${isOverridden ? styles.overridden : ''}`}>
                  <div className={styles['flag-meta']}>
                    <span className={styles['flag-name']}>{FLAG_LABEL[flagKey] ?? flagKey}</span>
                    {FLAG_LABEL[flagKey] && <span className={styles['flag-key']}>{flagKey}</span>}
                  </div>

                  <div className={styles.controls}>
                    {FEATURE_FLAG_VARIANTS[flagKey] ? (
                      <select
                        className={styles.select}
                        value={flagKey in overrides ? String(overrides[flagKey]) : ''}
                        onChange={(e) => handleSelectChange(flagKey, e.target.value)}
                      >
                        <option value="">— server default —</option>
                        {FEATURE_FLAG_VARIANTS[flagKey]!.map((variant) => (
                          <option key={variant} value={variant}>
                            {variant}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <>
                        <input
                          className={styles.input}
                          value={inputValues[flagKey] ?? ''}
                          onChange={(e) => setInputValues((prev) => ({ ...prev, [flagKey]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && applyOverride(flagKey)}
                          placeholder="variant or true/false"
                        />
                        <button className={styles['btn-apply']} onClick={() => applyOverride(flagKey)}>
                          Apply
                        </button>
                        {isOverridden && (
                          <button
                            className={styles['btn-reset']}
                            onClick={() => resetOverride(flagKey)}
                            title={`Server: ${serverValue || 'unset'}`}
                          >
                            ↩
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {isOverridden && serverValue !== (inputValues[flagKey] ?? '') && (
                    <span className={styles['server-value']}>server: {serverValue || 'unset'}</span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <button className={styles.toggle} onClick={() => setIsOpen((v) => !v)} title="Toggle feature flags overlay">
        🦔
        {Object.keys(overrides).length > 0 && <span className={styles['override-count']}>{Object.keys(overrides).length}</span>}
      </button>
    </div>
  )
}

export default FeatureFlagOverlay
